/**
 * Services from a container image (vfs-service src/routes/imageServices.routes.js): the curated picker and adding one.
 *
 * - `getImageCatalog(projectId)` → `{ docker, dockerOff: 'cluster'|'plan'|null, dockerOffMessage, images: [{ id, name,
 *   description, image, port, protocol?, ports?, env?, args?, volume?, memory, connect }] }`
 * - `addImageService(projectId, { name, from?, image?, port?, protocol?, ports?, env?, volume?, memory? })` →
 *   `{ service, spec, digest, note?, env, connect? }` (editors; 409 `code: 'exists' | 'docker_off'`)
 */
import config from '../config';
import { getAuthorizedFetch } from '../utils/fetchRegistry';
import { buildAclContextHeaders } from '../utils/aclContext';

const base = () => (config.vfsServiceUrl || '').replace(/\/$/, '');
const url = (projectId, path) => `${base()}/api/v1/projects/${encodeURIComponent(projectId)}/image-services${path}`;

export class ImageServicesError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = 'ImageServicesError';
    this.status = status;
    this.code = body?.code || null;
    this.reason = body?.reason || null;
  }
}

async function call(target, { method = 'GET', body } = {}) {
  if (!base()) { throw new ImageServicesError('vfs-service is not configured', 0, null); }
  const json = body !== undefined;
  const response = await getAuthorizedFetch()(target, {
    method,
    headers: { Accept: 'application/json', ...buildAclContextHeaders(), ...(json ? { 'Content-Type': 'application/json' } : {}) },
    ...(json ? { body: JSON.stringify(body) } : {}),
  });
  const data = await response.json().catch(() => null);
  if (!response.ok) { throw new ImageServicesError(data?.error || `Request failed (${response.status})`, response.status, data); }
  return data || {};
}

export const getImageCatalog = (projectId) => call(url(projectId, '/catalog'));
export const addImageService = (projectId, input) => call(url(projectId, ''), { method: 'POST', body: input });
